/**
 * Owner-only page for issuing a new Stripe Checkout link.
 *
 * Wraps POST /admin/clients with a tiny form (email, name, tier) and renders
 * the returned checkout_url with copy / mailto / open-in-new-tab affordances.
 * The owner emails this URL to the prospect manually (per ARCHITECTURE
 * decision #7, "Flavor 2: manual issuance"). When the prospect pays, the
 * customer.subscription.created webhook handles the rest.
 *
 * Access control: only Clerk users whose primary email matches OWNER_EMAIL
 * can call POST /admin/clients. We still render this page for everyone who
 * is signed in — the API returns 403 forbidden and we show that error
 * inline. We could route-guard here too, but reading the user's email from
 * Clerk's user object adds enough indirection that a server error message is
 * the simpler source of truth.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { AppShell } from '../components/AppShell';
import { useApi, ApiError } from '../lib/api';

type Tier = 'standard' | 'premium';
type Mode = Tier | 'comp';

type CreateClientResponse = {
  checkout_url: string;
  session_id: string;
  expires_at: number;
  tier: Tier;
  price_id: string;
};

type CompResponse = {
  client_account_id: string;
  setup_url: string;
  plan_tier: Tier;
  stripe_status: 'comp';
  welcome_email: { ok: boolean; id?: string; error?: string };
};

const TIER_PRICING: Record<Tier, { label: string; price: string }> = {
  standard: { label: 'Standard', price: '$497 / mo' },
  premium: { label: 'Premium', price: '$997 / mo' },
};

export function NewClient() {
  const { request } = useApi();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [mode, setMode] = useState<Mode>('standard');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateClientResponse | null>(null);
  const [compResult, setCompResult] = useState<CompResponse | null>(null);
  const [copied, setCopied] = useState(false);

  // Tier the comp account inherits (defaults to standard but the owner can
  // bump comps to premium-feature parity from the UI).
  const [compTier, setCompTier] = useState<Tier>('standard');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    setCompResult(null);
    setCopied(false);
    try {
      if (mode === 'comp') {
        // Comp path: skip Stripe entirely, provision directly.
        const res = await request<CompResponse>('/admin/clients/comp', {
          method: 'POST',
          body: JSON.stringify({
            email: email.trim(),
            name: name.trim(),
            plan_tier: compTier,
          }),
        });
        setCompResult(res);
      } else {
        const res = await request<CreateClientResponse>('/admin/clients', {
          method: 'POST',
          body: JSON.stringify({
            email: email.trim(),
            name: name.trim(),
            tier: mode,
          }),
        });
        setResult(res);
      }
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { error?: string; detail?: string } | null;
        setError(
          body?.detail ?? body?.error ?? `${e.status}: ${(e as Error).message}`,
        );
      } else {
        setError((e as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function copyUrl() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.checkout_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers — fall through silently. The visible URL is selectable.
    }
  }

  function startOver() {
    setResult(null);
    setCompResult(null);
    setEmail('');
    setName('');
    setMode('standard');
    setCompTier('standard');
    setError(null);
  }

  // -- Build the mailto: link the owner can click to fire up their mail
  //    client pre-filled with the prospect's address + the checkout URL.
  const mailtoHref = result
    ? `mailto:${encodeURIComponent(email)}` +
      `?subject=${encodeURIComponent('Your GroupPulse subscription link')}` +
      `&body=${encodeURIComponent(
        `Hi${name ? ' ' + name : ''},\n\n` +
          `Here's your subscription link for GroupPulse (` +
          `${TIER_PRICING[result.tier].label} — ${TIER_PRICING[result.tier].price}` +
          `):\n\n${result.checkout_url}\n\n` +
          `Once you complete checkout you'll receive a welcome email with the next steps (dashboard, Chrome extension, and a link to book your onboarding call).\n\n` +
          `Talk soon,\nMichael`,
      )}`
    : '#';

  return (
    <AppShell userRole="owner">
      <main className="page">
        <div className="page-hero">
          <div className="page-hero-icon" aria-hidden="true">➕</div>
          <div className="page-hero-text">
            <h1 className="page-hero-title">Add a new client</h1>
            <p className="page-hero-sub">
              Generates a Stripe Checkout link you can email. When they pay,
              GroupPulse provisions and emails them automatically.
            </p>
          </div>
        </div>
        {!result && !compResult ? (
          <section className="card">
            <h1>Add a new client</h1>
            <p className="muted">
              Two paths: <strong>Standard / Premium</strong> generate a Stripe
              Checkout link the prospect pays. <strong>Free / Comp</strong>{' '}
              skips Stripe entirely and provisions the account directly — use
              for friends, family, beta testers, or anyone you want on the
              house.
            </p>

            <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
              <Field label="Email" htmlFor="email">
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="off"
                  placeholder="prospect@theiragency.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Name (optional)" htmlFor="name">
                <input
                  id="name"
                  type="text"
                  autoComplete="off"
                  placeholder="Jane Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Plan" htmlFor="mode">
                <select
                  id="mode"
                  value={mode}
                  onChange={(e) => setMode(e.target.value as Mode)}
                  style={inputStyle}
                >
                  <option value="standard">
                    {TIER_PRICING.standard.label} —{' '}
                    {TIER_PRICING.standard.price}
                  </option>
                  <option value="premium">
                    {TIER_PRICING.premium.label} —{' '}
                    {TIER_PRICING.premium.price}
                  </option>
                  <option value="comp">Free / Comp — $0 (skip Stripe)</option>
                </select>
              </Field>

              {mode === 'comp' && (
                <Field label="Feature tier for the comp account" htmlFor="compTier">
                  <select
                    id="compTier"
                    value={compTier}
                    onChange={(e) => setCompTier(e.target.value as Tier)}
                    style={inputStyle}
                  >
                    <option value="standard">
                      Standard features (5 groups, 1 seat)
                    </option>
                    <option value="premium">
                      Premium features (unlimited groups, multi-seat)
                    </option>
                  </select>
                  <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                    Comp accounts get the same feature surface as paid clients —
                    this just decides which capability tier they get without
                    being billed.
                  </p>
                </Field>
              )}

              {error && <p style={errorStyle}>{error}</p>}

              <div className="actions" style={{ justifyContent: 'flex-start' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting || !email.trim()}
                >
                  {submitting
                    ? mode === 'comp'
                      ? 'Provisioning…'
                      : 'Generating link…'
                    : mode === 'comp'
                      ? 'Provision free account'
                      : 'Generate checkout link'}
                </button>
              </div>
            </form>
          </section>
        ) : compResult ? (
          <section className="card">
            <h1>✓ Comp account provisioned</h1>
            <p className="muted">
              <strong>{email}</strong> has been added as a comp account at the{' '}
              <strong>{compResult.plan_tier}</strong> feature tier. We didn&apos;t
              charge them — they&apos;ll see <code>comp</code> in your client
              status pills.
            </p>
            <ul style={{ fontSize: 14, lineHeight: 1.8 }}>
              <li>
                Welcome email:{' '}
                {compResult.welcome_email.ok ? (
                  <span style={{ color: '#047857' }}>
                    ✓ sent (id {compResult.welcome_email.id})
                  </span>
                ) : (
                  <span style={{ color: '#b91c1c' }}>
                    failed — {compResult.welcome_email.error}
                  </span>
                )}
              </li>
              <li>Default profile created.</li>
              <li>Setup link generated (also in the welcome email).</li>
            </ul>

            <div className="actions" style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
              <Link
                to={`/admin/clients/${compResult.client_account_id}`}
                className="btn btn-primary"
              >
                Configure their profile →
              </Link>
              <button className="btn btn-secondary" onClick={startOver}>
                Add another
              </button>
            </div>
          </section>
        ) : result ? (
          <section className="card">
            <h1>Checkout link ready</h1>
            <p className="muted">
              Email this to <strong>{email}</strong> — the link expires when
              Stripe closes the session (
              {new Date(result.expires_at * 1000).toLocaleString()}). Plan:{' '}
              <strong>{TIER_PRICING[result.tier].label}</strong> (
              {TIER_PRICING[result.tier].price}).
            </p>

            <div style={urlBoxStyle}>{result.checkout_url}</div>

            <div className="actions" style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={copyUrl}>
                {copied ? 'Copied ✓' : 'Copy URL'}
              </button>
              <a className="btn btn-secondary" href={mailtoHref}>
                Send via email
              </a>
              <a
                className="btn btn-secondary"
                href={result.checkout_url}
                target="_blank"
                rel="noreferrer"
              >
                Open in new tab
              </a>
              <button className="btn btn-secondary" onClick={startOver}>
                Add another
              </button>
            </div>
          </section>
        ) : null}
      </main>
    </AppShell>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        htmlFor={htmlFor}
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          marginBottom: 6,
          color: 'var(--text)',
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 15,
  fontFamily: 'inherit',
  background: '#fff',
  color: 'var(--text)',
};

const errorStyle: React.CSSProperties = {
  color: '#b91c1c',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 14,
  marginBottom: 16,
};

const urlBoxStyle: React.CSSProperties = {
  background: '#f1f5f9',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '12px 14px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 12,
  wordBreak: 'break-all',
  margin: '12px 0 20px',
  userSelect: 'all',
};
